<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :ref="question.questionId || question.questionComponent"
      :key="question.questionId || question.questionComponent"
      product-name="car"
      step-id="drivers"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      step-type="multiple"
      :step-index="driverIndex"
      @show-modal="showConfirmModal"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import { inject } from '@vue/composition-api';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-car-step-2',
  inject: ['config', 'helpers'],
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    driverIndex: {
      type: Number,
      required: true,
    },
  },
  data () {
    return {
      stepData: null,
    };
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
    // for fixStickyContainer
    'formObserver.valid': {
      immediate: true,
      handler (val) {
        if (val) this.$emit('valid');
      },
    },
  },
  async created () {
    if (this.driverIndex > 0 && this.driverIndex < 5) {
      // additional driver
      this.$emit('tracking', 'driver-additional-' + this.driverIndex);
    } else {
      // standard driver tracking action
      this.$emit('tracking', 'driver');
    }
    await this.initStep();

    this.formObserver.reset();
  },
  computed: {
    medicalConditionsHelpText () {
      const getHelpTextValue = (isAdditional) => `
        <p>
            If ${isAdditional ? 'they' : 'you'} are unsure if ${isAdditional ? 'they' : 'you'} have any medical conditions that will affect ${isAdditional ? 'their' : 'your'} driving, please ${isAdditional ? 'ask them to ' : ''} check the DVLA website or ask a doctor and inform the DVLA if necessary.
        </p>
        <p>
          If you have not declared a condition or informed the DVLA and ${isAdditional ? 'either of ' : ''}you make a claim, then your claim may be refused and your insurance will be void.
        </p>
      `;

      return {
        value: getHelpTextValue(false),
        valueAdditional: getHelpTextValue(true),
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Declaring medical conditions',
      };
    },
    periodOfLicenceHeldHelpText () {
      const getHelpTextTitle = (isAdditional) => `The amount of years selected needs to be the number of full years since ${isAdditional ? 'they' : 'you'} passed ${isAdditional ? 'their' : 'your'} practical driving test e.g. If ${isAdditional ? 'they' : 'you'} passed ${isAdditional ? 'their' : 'your'} test 4 years and 11 months ago, ${isAdditional ? 'they' : 'you'} must answer 4 years.`;
      const getHelpTextValue = (isAdditional) => `
        <p>${isAdditional ? 'They' : 'You'} will need to enter the month ${isAdditional ? 'they' : 'you'} passed ${isAdditional ? 'their' : 'your'} test if it was less than 4 years ago. If ${isAdditional ? 'they' : 'you'} haven’t passed ${isAdditional ? 'their' : 'your'} test yet, this needs to be the number of years since ${isAdditional ? 'they' : 'you'} received ${isAdditional ? 'their' : 'your'} provisional licence.</p>
        <p>On a UK  licence, ${isAdditional ? 'they' : 'you'} can find the ‘valid from’ date on the back of ${isAdditional ? 'their' : 'your'} licence.</p>
      `;

      return {
        title: getHelpTextTitle(false),
        titleAdditional: getHelpTextTitle(true),
        value: getHelpTextValue(false),
        valueAdditional: getHelpTextValue(true),
        collapsible: true,
        openerText: this.isExperimentMinimiseHelptext ? 'Providing an accurate licence date' : 'Show More',
        closerText: this.isExperimentMinimiseHelptext ? 'Providing an accurate licence date' : 'Hide',
      };
    },
    questions () {
      return [
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'title',
          params: {
            customFields: {
              field: 'title',
            },
            title: 'What is your title?',
            titleAdditional: 'What is their title?',
            helpText: {
              value:
                'If your title is not in the list, please select the most appropriate for you.',
              valueAdditional:
                'If their title is not in the list, please select the most appropriate for them',
            },
            optionsConfig: {
              options: this.helpers.getTitleOptions(this.isMxDrLiveCar),
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select a title.',
              },
            },
          },
        },
        {
          questionComponent: 'question-name',
          questionId: 'name',
        },
        {
          questionComponent: 'question-date-of-birth',
          questionId: 'date-of-birth',
          params: {
            componentType: this.isDOBTextField ? 'input' : 'select',
          },
        },
        {
          questionComponent: 'question-uk-resident',
          questionId: 'uk-resident',
        },
        {
          questionComponent: 'question-marital-status',
          questionId: 'marital-status',
          params: {
            helpText: `
              <p>
                There might be several options that fit you - so choose the way that you would like to be described.
              </p>
              <p>
                Common law includes cohabiting/living with a partner.
              </p>
            `,
          },
        },
        {
          questionComponent: 'question-relationship-proposer',
          questionId: 'relationship-proposer',
          params: {
            onlyForAdditionals: true,
            validationErrors: {
              relationshipProposer: {
                is_not: 'Please confirm their relationship to you.',
              },
            },
          },
        },
        {
          questionComponent: 'question-children',
          questionId: 'children',
        },
        {
          questionComponent: 'question-telephone',
          questionId: 'telephone',
          params: {
            hiddenForAdditionals: true,
            helpText: {
              value: this.isExperimentMinimiseHelptext && 'Your insurance provider may need to contact you to discuss your policy. Later in this form, we’ll also ask you if you want to share your number with the providers who offer you the lowest prices, to discuss your insurance needs before you buy (this is a free service and completely optional).',
              openerText: 'Why do I need to enter a phone number?',
              collapsible: this.isExperimentMinimiseHelptext,
            },
          },
        },
        {
          questionComponent: 'question-address',
          questionId: 'address',
          params: {
            hiddenForAdditionals: true,
          },
        },
        {
          questionComponent: 'question-kept-address',
          questionId: 'kept-address',
          params: {
            defaultValues: {
              keptAddress: true,
            },
          },
        },
        {
          questionComponent: 'question-residential-status',
          questionId: 'residential-status',
        },
        {
          questionComponent: (() => {
            return `question-home-renewal${this.homeRenewalNew ? '-split-testing' : ''}`;
          })(),
          questionId: 'home-renewal',
        },
        {
          questionComponent: 'question-email',
          questionId: 'email',
        },
        {
          questionComponent: 'question-employment-status',
          questionId: 'employment-status',
        },
        {
          questionComponent: 'question-occupation',
          questionId: 'occupation',
        },
        {
          questionComponent: 'question-business',
          questionId: 'business',
        },
        {
          questionComponent: 'question-another-job',
          questionId: 'another-job',
          params: {
            defaultValues: {
              partTimeOccupation: false,
            },
          },
        },
        {
          questionComponent: 'question-type-of-driving',
          questionId: 'type-of-driving',
        },
        {
          questionComponent: 'question-use-of-other-vehicles',
          questionId: 'use-of-other-vehicles',
          params: {
            onlyForAdditionals: true,
          },
        },
        {
          questionComponent: 'question-period-of-licence-held',
          questionId: 'period-of-licence-held',
          params: {
            optionsConfig: {
              optionsAmount: 61,
            },
            helpText: this.periodOfLicenceHeldHelpText,
          },
        },
        {
          questionComponent: 'question-licence-number',
          questionId: 'licence-number',
        },
        {
          questionComponent: 'question-driving-qualifications',
          questionId: 'driving-qualifications',
          params: {
            title: 'Do you have any additional driving qualifications?',
            helpText: {
              value: 'Some of our insurance providers may give a discount if you have an additional driving qualification.',
              priceSensitive: true,
            },
            hiddenForAdditionals: true,
            defaultValues: {
              driverPassPlus: false,
              advancedMotorist: false,
            },
            hiddenValues: {
              driverPassPlus: false,
              advancedMotorist: false,
            },
            showRules: {
              typeOfDriving: {
                isAny: {
                  other: ['F'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-classic-car-club',
          questionId: 'classic-car-club',
          params: {
            customFields: {
              field: 'classicCarClubMember',
            },
            title: 'Are you a member of a classic car club?',
            helpText: 'Some insurers may give a discount if you are a member of classic club.',
            hiddenForAdditionals: true,
            defaultValues: {
              classicCarClubMember: null,
            },
            validationRules: {
              field: {
                required: false,
              },
            },
          },
        },
        {
          questionComponent: 'question-medical-conditions',
          questionId: 'medical-conditions',
          params: {
            helpText: this.medicalConditionsHelpText,
          },
        },
        {
          questionComponent: 'question-base-claims',
          questionId: 'claims',
          params: {
            customFields: [
              'hadAnyAccidents',
              'claims',
              'claims-new',
            ],
            defaultValues: {
              claims: [],
            },
            title: 'Have you had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            titleAdditional: 'Have they had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            helpText: {
              value: "Insurers need to know about all claims and incidents in the last 5 years, even if it wasn't your fault or if you were driving another vehicle.",
              valueAdditional: "Insurers need to know about all claims and incidents in the last 5 years, even if it wasn't their fault or if they were driving another vehicle.",
            },
            questions: [
              {
                questionId: 'date-of-claim',
                questionComponent: 'question-base-date',
                params: {
                  customFields: {
                    field: 'dateOfClaim',
                  },
                  title: 'When did the incident happen?',
                  prevalidationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter the date.',
                      dateOfIncident: 'The incident must be within the last five years.',
                      checkFutureIncident: 'The incident date must not be in the future.',
                    },
                  },
                },
              },
              {
                questionId: 'cost-of-repairs',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'costOfRepairs',
                  },
                  title: 'What was the total claim cost?',
                  helpText: {
                    value: 'If you are unsure your insurance company at the time of the incident should be able to give you this figure. If the claim is still to be settled they may be able to give a reserve figure.',
                    valueAdditional: 'If you are unsure their insurance company at the time of the incident should be able to give you this figure. If the claim is still to be settled they may be able to give a reserve figure.',
                  },
                  inputPrefix: '£',
                  placeholder: 'e.g. 250',
                  minLength: 1,
                  maxLength: 7,
                  prevalidationRules: {
                    field: {
                      required: true,
                      maxValue: 9999999,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      maxValue: 9999999,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'The Cost of Repairs field is required.',
                    },
                  },
                },
              },
              {
                questionId: 'cause-of-description',
                questionComponent: 'question-base-radio-list',
                params: {
                  customFields: {
                    field: 'causeOfDescription',
                  },
                  title: 'What kind of incident or claim was it?',
                  optionsConfig: {
                    mainItems: ['A', 'W', 'T'],
                    otherLabel: 'Other',
                    options: {
                      A: 'Accident',
                      W: 'Windscreen/Glass',
                      T: 'Theft',
                      F: 'Fire',
                      O: 'Storm Damage',
                      V: 'Vandalism',
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please select option.',
                    },
                  },
                },
              },
              {
                questionId: 'settled-claim',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'settledClaim',
                  },
                  title: 'Was the claim settled?',
                  defaultValues: {
                    field: false,
                  },
                  showRules: {
                    causeOfDescription: {
                      isAny: {
                        other: ['A'],
                      },
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'any-injuries',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'anyInjuries',
                  },
                  title: 'Were there any injuries?',
                  defaultValues: {
                    field: false,
                  },
                  showRules: {
                    causeOfDescription: {
                      isAny: {
                        other: ['A'],
                      },
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'single-accident',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'singleAccident',
                  },
                  title: 'Was it a single vehicle accident?',
                  helpText: 'If this was a single vehicle accident this means no other vehicles were involved.',
                  defaultValues: {
                    field: true,
                  },
                  showRules: {
                    settledClaim: {
                      isAny: {
                        other: [true],
                      },
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'at-fault',
                questionComponent: 'question-base-dropdown',
                params: {
                  customFields: {
                    field: 'atFault',
                  },
                  title: 'Who was at fault?',
                  helpText: "Please tell us who was at fault. If your vehicle was involved in a hit and run accident, or you are unsure who was at fault, please select 'Our driver'.",
                  optionsConfig: {
                    options: {
                      C: 'Our Driver',
                      O: 'Other Party',
                      B: 'Both Parties',
                    },
                  },
                  showRules: {
                    singleAccident: {
                      isAny: {
                        other: [false],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please select an option from this list.',
                    },
                  },
                },
              },
              {
                questionId: 'type-theft',
                questionComponent: 'question-base-radio-list',
                params: {
                  customFields: {
                    field: 'typeTheft',
                  },
                  title: 'What was stolen?',
                  optionsConfig: {
                    options: {
                      VC: 'Vehicle and The Contents',
                      C: 'Just The Contents of The Vehicle',
                    },
                  },
                  showRules: {
                    causeOfDescription: {
                      isAny: {
                        other: ['T'],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'recovered-vehicle',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'recoveredVehicle',
                  },
                  title: 'Was the vehicle recovered?',
                  defaultValues: {
                    field: true,
                  },
                  showRules: {
                    typeTheft: {
                      isAny: {
                        other: ['VC'],
                      },
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'theft-from-home',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'theftFromHome',
                  },
                  title: 'Did the theft happen from your home?',
                  titleAdditional: 'Did the theft happen from their home?',
                  defaultValues: {
                    field: true,
                  },
                  showRules: {
                    typeTheft: {
                      isAny: {
                        other: ['VC', 'C'],
                      },
                      required: true,
                    },
                  },
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-base-convictions',
          questionId: 'convictions',
          params: {
            customFields: [
              'hadAnyConvictions',
              'convictions',
              'convictions-new',
            ],
            defaultValues: {
              convictions: [],
            },
            title: 'Have you had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            titleAdditional: 'Have they had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            helpText: {
              value: 'If unsure, conviction details can be found by viewing your driving licence information on the government website or you can also check via phone or post.',
              valueAdditional: 'If unsure, conviction details can be found by viewing their driving licence information on the government website or you can also check via phone or post.',
            },
            questions: [
              {
                questionId: 'date_of_conviction',
                questionComponent: 'question-base-date',
                params: {
                  customFields: {
                    field: 'dateOfConviction',
                  },
                  title: 'What date did the conviction happen?',
                  helpText: {
                    value: 'If unsure, conviction details can be found by viewing your driving licence information on the government website or you can also check via phone or post.',
                    valueAdditional: 'If unsure, conviction details can be found by viewing their driving licence information on the government website or you can also check via phone or post.',
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                      dateOfIncident: true,
                      checkFutureIncident: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter the date.',
                      dateOfIncident: 'The conviction must be within the last five years.',
                      checkFutureIncident: 'The conviction date must not be in the future.',
                    },
                  },
                },
              },
              {
                questionId: 'conviction-declared-accident',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'convictionDeclaredAccident',
                  },
                  title: 'Does this conviction relate to a declared accident?',
                  helpText: {
                    value: 'Is this conviction related to any accidents you have had in the past 5 years?',
                    valueAdditional: 'Is this conviction related to any accidents they have had in the past 5 years?',
                  },
                },
              },
              {
                questionId: 'offence-code',
                questionComponent: 'question-base-search-other',
                params: {
                  customFields: {
                    field: 'offenceCode',
                    search: 'offenceCode',
                  },
                  title: 'What was the conviction for?',
                  helpText: {
                    value: 'The code for your conviction may be on your driving licence. For more information on convictions from the past four years, please visit the DVLA website.',
                    valueAdditional: 'The code for they conviction may be on they driving licence. For more information on convictions from the past four years, please visit the DVLA website.',
                  },
                  searchType: 'getConvictionOffenceCodes',
                  topGroup: 'most_common_offence_codes',
                  isImmediate: true,
                  optionsConfig: {
                    placeholder: 'Please Select Conviction Code',
                    isCapitalize: true,
                  },
                  validationErrors: {
                    search: {
                      required: 'Please confirm the offence code.',
                    },
                  },
                },
              },
              {
                questionId: 'alcohol-method-test',
                questionComponent: 'question-base-radio-list',
                params: {
                  customFields: {
                    field: 'alcoholMethodTest',
                  },
                  title: 'What method was used to test for alcohol?',
                  optionsConfig: {
                    options: {
                      B: 'Breath',
                      L: 'Blood',
                      U: 'Urine',
                    },
                  },
                  showRules: {
                    offenceCode: {
                      isAny: {
                        other: [
                          'CD40',
                          'CD60',
                          'DR10',
                          'DR20',
                          'DR50',
                          'DR40',
                        ],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                },
              },
              {
                questionId: 'alcohol-level',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'alcoholLevel',
                  },
                  title: 'What was the alcohol level recorded (mg/100 ml)?',
                  placeholder: 'e.g. 90',
                  minLength: 1,
                  maxLength: 3,
                  withoutSeparators: true,
                  showRules: {
                    offenceCode: {
                      isAny: {
                        other: [
                          'CD40',
                          'CD60',
                          'DR10',
                          'DR20',
                          'DR50',
                          'DR40',
                        ],
                      },
                      required: true,
                    },
                  },
                  prevalidationRules: {
                    field: {
                      minValue: 1,
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      minValue: 1,
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      minValue: 'Please enter the alcohol level e.g. 90.',
                      required: 'Please enter the alcohol level e.g. 90.',
                    },
                  },
                },
              },
              {
                questionId: 'points-received',
                questionComponent: 'question-base-dropdown',
                params: {
                  customFields: {
                    field: 'pointsReceived',
                  },
                  title: 'How many penalty points did you get for this offence?',
                  titleAdditional: 'How many penalty points did they get for this offence?',
                  helpText: 'Enter the number of penalty points associated with the specific conviction only, not the total number of points on your licence.',
                  optionsConfig: {
                    placeholder: 'Please select no. of points',
                    options: [
                      {
                        value: 0,
                        text: '0 Points',
                      },
                      {
                        value: 1,
                        text: '1 Point',
                      },
                      {
                        value: 2,
                        text: '2 Points',
                      },
                      {
                        value: 3,
                        text: '3 Points',
                      },
                      {
                        value: 4,
                        text: '4 Points',
                      },
                      {
                        value: 5,
                        text: '5 Points',
                      },
                      {
                        value: 6,
                        text: '6 Points',
                      },
                      {
                        value: 7,
                        text: '7 Points',
                      },
                      {
                        value: 8,
                        text: '8 Points',
                      },
                      {
                        value: 9,
                        text: '9 Points',
                      },
                      {
                        value: 10,
                        text: '10 Points',
                      },
                      {
                        value: 11,
                        text: '11 Points',
                      },
                      {
                        value: 12,
                        text: '12 Points',
                      },
                    ],
                  },
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please select an option from this list.',
                    },
                  },
                },
              },
              {
                questionId: 'how-much-you-fined',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'howMuchYouFined',
                  },
                  title: 'How much were you fined?',
                  titleAdditional: 'How much were they fined?',
                  placeholder: 'e.g. 100',
                  minLength: 0,
                  maxLength: 5,
                  inputPrefix: '£',
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter how much were you fined.',
                    },
                  },
                },
              },
              {
                questionId: 'received-ban-period',
                questionComponent: 'question-base-number',
                params: {
                  customFields: {
                    field: 'receivedBanPeriod',
                  },
                  title: 'If you received a ban, how many months was this for? (please enter 0 if you did not receive any ban)',
                  titleAdditional: 'If they received a ban, how many months was this for? (please enter 0 if they did not receive any ban)',
                  placeholder: 'Please enter no. of months',
                  minLength: 0,
                  maxLength: 2,
                  prevalidationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationRules: {
                    field: {
                      required: true,
                    },
                  },
                  validationErrors: {
                    field: {
                      required: 'Please enter how many months were you banned for.',
                    },
                  },
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'had-any-non-mot-convictions',
          params: {
            customFields: {
              field: 'hadAnyNonMotConvictions',
            },
            title:
              'Have you got any unspent non-motoring-related criminal convictions?',
            titleAdditional:
              'Do they have any unspent non-motoring-related criminal convictions',
            helpText:
              "An unspent conviction is any conviction on a persons' record which hasn't passed the time of the relevant rehabilitation period.",
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['showToast', 'showConfirmModal']),
    async initStep () {
      this.stepData = cloneDeep(this.form.drivers[this.driverIndex]);
    },
  },
  setup () {
    const {
      homeRenewalNew,
      isDOBTextField,
      isMxDrLiveCar,
    } = inject('abTastyControllerUI');

    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      homeRenewalNew,
      isDOBTextField,
      isMxDrLiveCar,
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
